/**
 * Session key mirroring Angular's `onboardingModalViewed`
 * (`dashboard-main.component.ts:319-320`).
 *
 * Raw `sessionStorage` is used deliberately, NOT the `setInStorage` /
 * `getFromStorage` helpers in `utils/index.ts`: those JSON-encode, so `'true'`
 * would be persisted as `"true"` and would not match Angular's plain string.
 * This follows the neighbouring dashboard gates (`CONTACT_POPUP_SHOWN_KEY`
 * in `pages/dashboard/index.tsx`, `justSignedIn` in
 * `useChildAgedOutSignInPopup`), which bypass the helpers for the same reason.
 *
 * Cleared on sign-out in `app/store.ts`, mirroring Angular's
 * `delete-session.service.ts:50`.
 */
export const ONBOARDING_MODAL_VIEWED_KEY = 'onboardingModalViewed';

/**
 * Upper bound on how long the tour waits for the Insights & Tips catalogue
 * before opening anyway (BRD §9.5).
 *
 * This wait MUST be bounded. `selectIsCatalogueLoaded` is `catalogue !== null`
 * and `fetchInsightsAndTipsCatalogue` has no catch that writes the slice, so a
 * failed request never settles and the fetch hook will not retry. An unbounded
 * gate would mean the tour never opens at all — and because `lastVisitedAt` is
 * stamped server-side on login regardless, that user would lose the tour
 * permanently.
 */
export const CATALOGUE_WAIT_MS = 2500;

/**
 * Spotlight inset and corner radius (antd `Tour` `gap` prop).
 *
 * `gap` is Tour-level only — rc-tour does not read it per step — so a step that
 * needs a different shape has this recomputed for it in `DashboardTour`. See
 * `TOUR_CIRCULAR_TARGET_SIZE`.
 */
export const TOUR_GAP = { offset: 8, radius: 12 } as const;

/**
 * Rendered size of the header avatar, used to derive a fully circular spotlight
 * for the account step: hole = size + 2*offset, so radius = size/2 + offset.
 * Derived rather than hard-coded so the circle survives an avatar resize.
 */
export const TOUR_CIRCULAR_TARGET_SIZE = 32;

/**
 * Anchor ids the tour spotlights.
 *
 * `navigation` reuses the id antd's `Sider` already renders. It exists on
 * desktop only — `WithSidebar` skips `<Sidebar>` entirely on mobile, where the
 * navigation lives in the Header's drawer instead. Mobile steps therefore
 * target `mobileNavigation`, and the tour opens that drawer first.
 */
export const DRAWER_TRANSITION_MS = 400;

export const TOUR_ANCHORS = {
  navigation: 'left-nav',
  mobileNavigation: 'tour-anchor-mobile-nav',
  mobileAccountItem: 'tour-anchor-mobile-account-item',
  account: 'tour-anchor-account',
  section: (sectionId: string) => `tour-anchor-${sectionId}`,
} as const;

/**
 * Tour copy — verbatim from the Figma frames. Figma is the authority; Angular
 * `en.yaml` is not consulted for this module.
 *
 * ⚠ BRD §12 is NOT a reliable source for this module. A desktop audit on
 * 2026-08-21 found its versions of the navigation, highlights, quick-links,
 * support, insights and finish bodies were all abridged — sometimes losing a
 * whole clause. Every string below was re-read from its frame; correct §12 to
 * match, not the reverse.
 *
 * Two things to preserve when editing:
 *  - All apostrophes are typographic (U+2019). Figma renders "Don’t show
 *    again" that way, and it also sidesteps a conflict in this repo's lint
 *    setup: `quotes: single` rejects double-quoted strings while prettier
 *    rejects escaped apostrophes inside single quotes, so a straight
 *    apostrophe has no lint-clean spelling here.
 *  - `TOUR_WELCOME.body` is a single fixed string. There is deliberately no
 *    `{redesigned/built}` variable: the gate admits only first-visit,
 *    non-migrated users, so the "existing user" wording is unreachable
 *    (BRD §9.4). Do not reintroduce a user-type branch.
 */
export const TOUR_WELCOME = {
  title: 'Welcome to your new dashboard!',
  // eslint-disable-next-line quotes
  body: "We've built your security hub to give you faster access to what matters most. This quick tour will show you where everything lives.",
} as const;

export const TOUR_NAVIGATION = {
  title: 'Find your way around',
  // eslint-disable-next-line quotes
  body: "Use the navigation menu to move between your main sections, so you can always find what you're looking for. Your current section stays highlighted as you go.",
} as const;

/**
 * Copy for the section-derived steps, keyed by resolved section id (BRD §9.3).
 *
 * A section id absent from this map yields NO step. That is deliberate: a new
 * dashboard section added to `dashboardRules.ts` will not silently render an
 * empty tooltip — it joins the tour only once copy is added here.
 */
export const TOUR_SECTION_COPY = {
  medicalMonitoring: {
    title: 'Monitor your medical claims',
    body: 'Medical Monitoring with HealthLock gives you a complete picture of your healthcare spending by letting you monitor your medical claims for potential billing errors.',
  },
  highlights: {
    title: 'Your highlights at-a-glance',
    body: 'The Highlights section brings your monitoring activity and account details together, giving you a quick view of your most important updates — all in one place.',
  },
  quickLinks: {
    title: 'Jump to key tools quickly',
    body: 'Quick Links provide easy access to the tools that help you manage your protection, update important details, and take action whenever you need to.',
  },
  support: {
    title: 'Help when you need it',
    body: 'The Support section brings helpful resources and answers to common questions together in one place, so you can find the information and guidance you need.',
  },
} as const;

/* Insights & Tips — the body differs by breakpoint. Unlike the welcome step,
   this is a genuine responsive variation: mobile names the section explicitly. */
export const TOUR_INSIGHTS = {
  title: 'Stay informed',
  body: 'Stay informed with curated articles covering security alerts, cyber crime trends, and financial tips — all tailored to help you stay one step ahead.',
} as const;

/* My account — the body differs by breakpoint: desktop points at the header
   avatar, mobile at the navigation menu item.
   The braces in the Figma text (`{profile icon in the top-right corner}`) are a
   placeholder marker, not literal copy, and the clause is NOT emphasised — it
   renders at the same weight as the rest of the sentence. */
export const TOUR_ACCOUNT = {
  title: 'Manage your account',
  bodyDesktop:
    'Select the profile icon in the top-right corner to access account settings, notification preferences, and subscription details.',
  bodyMobile:
    'Select My account in navigation menu to access account settings, notification preferences, and subscription details.',
} as const;

export const TOUR_FINISH = {
  // eslint-disable-next-line quotes
  title: "You're ready to go!",
  body: 'You now know how to access key features, view your account highlights, and contact our support team when you need them. Explore your dashboard and check out all the features available to you.',
  cta: 'Explore your dashboard',
} as const;

export const TOUR_ACTIONS = {
  // eslint-disable-next-line quotes
  dontShowAgain: "Don't show again",
  skip: 'Skip',
  back: 'Back',
  next: 'Next',
  finish: 'Finish',
} as const;
